import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useVoiceMemoRecorder from '@treams/treams-app/src/components/voice-memos/useVoiceMemoRecorder';
import { useToasts } from '@treams/treams-ui';

function ActiveRecorder({
	stream,
	onSample,
	onAmplitude,
	onFinish,
	onError,
	stopRef,
}: {
	stream: MediaStream;
	onSample: (amplitude: number) => void;
	onAmplitude: (amplitude: number) => void;
	onFinish: (blob: Blob) => void;
	onError: (error: Error) => void;
	stopRef: React.MutableRefObject<(() => void) | null>;
}) {
	const recorder = useVoiceMemoRecorder({
		stream,
		sampleRate: 1.0,
		onAmplitude,
		onSample,
		onFinish,
		onError,
	});

	useEffect(() => {
		stopRef.current = () => recorder.stop();
	}, [recorder, stopRef]);

	return null;
}

export type UseRecorderResult = {
	recording: boolean;
	countdown: number | null;
	elapsed: number;
	bars: number[];
	current: number;
	recorderElement: ReactNode;
	requestMicrophone: () => Promise<boolean>;
	stopRecording: () => void;
	cancelRecording: () => void;
};

export function useRecorder(onRecordingComplete: (blob: Blob) => void): UseRecorderResult {
	const { addToast } = useToasts();
	const { t } = useTranslation();

	const [recording, setRecording] = useState(false);
	const [countdown, setCountdown] = useState<number | null>(null);
	const [elapsed, setElapsed] = useState(0);
	const [bars, setBars] = useState<number[]>([]);
	const [current, setCurrent] = useState(0);
	const [stream, setStream] = useState<MediaStream | undefined>();
	const [recordingStream, setRecordingStream] = useState<MediaStream | undefined>();

	const recorderStopRef = useRef<(() => void) | null>(null);
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const countdownRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const wakeLockRef = useRef<WakeLockSentinel | null>(null);
	const onCompleteRef = useRef(onRecordingComplete);
	onCompleteRef.current = onRecordingComplete;

	useEffect(() => {
		if (countdown === null) return;

		if (countdown === 0) {
			setCountdown(null);
			setRecording(true);
			setRecordingStream(stream);

			return;
		}

		countdownRef.current = setTimeout(() => {
			setCountdown((prev) => (prev !== null ? prev - 1 : null));
		}, 1000);

		return () => {
			if (countdownRef.current) clearTimeout(countdownRef.current);
		};
	}, [countdown, stream]);

	useEffect(() => {
		if (recording) {
			intervalRef.current = setInterval(() => {
				setElapsed((prev) => prev + 1);
			}, 1000);
		} else {
			if (intervalRef.current) clearInterval(intervalRef.current);
			setElapsed(0);
		}

		return () => {
			if (intervalRef.current) clearInterval(intervalRef.current);
		};
	}, [recording]);

	useEffect(() => {
		if (!recording || !('wakeLock' in navigator)) return;

		let released = false;

		const acquire = () => {
			navigator.wakeLock
				.request('screen')
				.then((sentinel) => {
					if (released) return sentinel.release().catch(() => {});

					wakeLockRef.current = sentinel;

					sentinel.addEventListener('release', () => {
						if (wakeLockRef.current === sentinel) wakeLockRef.current = null;
					});
				})
				.catch(() => {});
		};

		const handleVisibility = () => {
			if (document.visibilityState === 'visible' && !wakeLockRef.current) acquire();
		};

		acquire();

		document.addEventListener('visibilitychange', handleVisibility);

		return () => {
			released = true;
			document.removeEventListener('visibilitychange', handleVisibility);
			wakeLockRef.current?.release().catch(() => {});
			wakeLockRef.current = null;
		};
	}, [recording]);

	const resetState = useCallback(() => {
		setRecording(false);
		setCountdown(null);
		setElapsed(0);
		setBars([]);
		setCurrent(0);
		setRecordingStream(undefined);
		setStream(undefined);
		recorderStopRef.current = null;
	}, []);

	useEffect(() => {
		if (!stream) return;

		const audioTrack = stream.getAudioTracks()[0];

		const handleTrackEnded = () => {
			resetState();
			addToast(t('voiceMemo.error.message'), {
				appearance: 'error',
				autoDismiss: true,
			});
		};

		audioTrack?.addEventListener('ended', handleTrackEnded);

		return () => {
			audioTrack?.removeEventListener('ended', handleTrackEnded);
			stream.getTracks().forEach((track) => track.stop());
		};
	}, [stream, resetState, addToast, t]);

	const requestMicrophone = useCallback(async (): Promise<boolean> => {
		try {
			const mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
			setStream(mediaStream);
			setCountdown(3);
			return true;
		} catch {
			addToast(t('transcription.failedToAccessMicrophone'), {
				appearance: 'error',
				autoDismiss: true,
			});

			return false;
		}
	}, [addToast, t]);

	const stopRecording = useCallback(() => {
		if (!recording) return;
		setRecording(false);
		recorderStopRef.current?.();
	}, [recording]);

	const cancelRecording = useCallback(() => {
		stream?.getTracks().forEach((track) => track.stop());
		resetState();
	}, [stream, resetState]);

	const recorderElement = recordingStream ? (
		<ActiveRecorder
			stream={recordingStream}
			onSample={(amplitude) => setBars((prev) => [...prev, amplitude])}
			onAmplitude={(amplitude) => setCurrent(amplitude)}
			onFinish={(blob) => {
				resetState();
				onCompleteRef.current(blob);
			}}
			onError={() => {
				resetState();
				addToast(t('voiceMemo.error.message'), {
					appearance: 'error',
					autoDismiss: true,
				});
			}}
			stopRef={recorderStopRef}
		/>
	) : null;

	return {
		recording,
		countdown,
		elapsed,
		bars,
		current,
		recorderElement,
		requestMicrophone,
		stopRecording,
		cancelRecording,
	};
}
